import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CalendlyButton, CalendlyInline } from '../components/CalendlyButton';
import { 
  Phone, 
  Mail, 
  MapPin,
  ArrowRight,
  Clock,
  Calendar
} from 'lucide-react';

const Contact = () => {
  return (
    <>
      <Helmet>
        <title>Contact | Mehdi Richa - Formateur & Auditeur Qualité Vaucluse</title>
        <meta 
          name="description" 
          content="Contactez Mehdi Richa, formateur et auditeur qualité dans le Vaucluse. Basé à Avignon, interventions dans tout le Vaucluse. Premier échange gratuit." 
        />
        <meta property="og:title" content="Contact - Mehdi Richa, Formateur & Auditeur Qualité" />
        <meta property="og:description" content="Contactez-moi pour discuter de votre projet de formation ou d'audit qualité dans le Vaucluse." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/contact`} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-slate-50 overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-slate-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
              Contact
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
              Contact
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Vous avez un projet de formation ou d'audit qualité ? Réservez directement 
              un créneau pour un premier échange gratuit, ou contactez-moi par email ou téléphone.
            </p>
          </div>
        </div>
      </section>

      {/* Calendly Booking Section */}
      <section className="py-16 bg-white" data-testid="contact-calendly">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              <Calendar className="w-4 h-4" />
              Nouveau
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Réservez un créneau en ligne
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Choisissez le créneau qui vous convient pour un premier échange de 30 minutes. 
              C'est gratuit et sans engagement.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto bg-slate-50 rounded-3xl p-4 md:p-8 animate-fade-in animation-delay-100">
            <CalendlyInline className="rounded-2xl overflow-hidden" />
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="contact-info">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Ou contactez-moi directement
            </h2>
            <p className="text-slate-600">
              Vous préférez m'écrire ou m'appeler ? Voici mes coordonnées.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-100" data-testid="contact-email-card">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 bg-blue-600 rounded-2xl flex items-center justify-center">
                  <Mail className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Email</h3>
                <a 
                  href="mailto:mehdi.richa.formation@gmail.com" 
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm break-all"
                  data-testid="contact-email-link"
                >
                  mehdi.richa.formation@gmail.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-200" data-testid="contact-phone-card">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 bg-blue-600 rounded-2xl flex items-center justify-center">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Téléphone</h3>
                <a 
                  href="tel:0620968980" 
                  className="text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="contact-phone-link"
                >
                  06 20 96 89 80
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-300" data-testid="contact-location-card">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 bg-blue-600 rounded-2xl flex items-center justify-center">
                  <MapPin className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Localisation</h3>
                <p className="text-slate-600 text-sm">
                  Avignon, Vaucluse (84)
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="max-w-2xl mx-auto mt-12 animate-fade-in animation-delay-400">
            <Card className="bg-white border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Délai de réponse</h3>
                    <p className="text-slate-600 text-sm">
                      Je m'efforce de répondre à toutes les demandes sous 24 à 48 heures ouvrées. 
                      Pour les demandes urgentes, n'hésitez pas à me joindre par téléphone.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="py-16 bg-white" data-testid="contact-zone">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 rounded-2xl p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Zone d'intervention</h3>
                  <p className="text-slate-600">Basé à Avignon, j'interviens dans tout le Vaucluse et également à distance</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {['Avignon', 'Carpentras', 'Orange', 'Cavaillon', 'Apt', 'L\'Isle-sur-la-Sorgue'].map((city) => (
                  <span 
                    key={city}
                    className="px-3 py-1 bg-white rounded-full text-sm text-slate-700 shadow-sm"
                  >
                    {city}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
